import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtFCFA, formatApiError, clean } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { WhatsAppButton } from "@/components/WhatsApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bed, Bath, Maximize, Car, MapPin, Heart, Share2, Calendar, CheckCircle2, ArrowLeft, ScrollText, Expand, ChevronLeft, ChevronRight } from "lucide-react";
import { BookingWidget } from "@/components/BookingWidget";
import { PropertyReviews } from "@/components/PropertyReviews";

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [fav, setFav] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [visit, setVisit] = useState({ name: "", phone: "", email: "", date: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/properties/${id}`);
        setP(data);
        setForm((f) => ({ ...f, message: `Bonjour, je suis intéressé(e) par le bien ${data.reference} - ${data.title}.` }));
      } catch { toast.error(t("detail.notFound")); }
      if (user) { try { const { data } = await api.get("/favorites"); setFav(data.some((x) => x.id === id)); } catch {} }
    })();
  }, [id, user]);

  const toggleFav = async () => {
    if (!user) { toast.error(t("detail.loginFav")); return; }
    try {
      if (fav) { await api.delete(`/favorites/${id}`); setFav(false); toast(t("detail.favRemove")); }
      else { await api.post(`/favorites/${id}`); setFav(true); toast.success(t("detail.favAdd")); }
    } catch {}
  };

  const share = async () => {
    try { await navigator.share({ title: p.title, url: window.location.href }); }
    catch { navigator.clipboard.writeText(window.location.href); toast.success(t("detail.linkCopied")); }
  };

  const imgCount = () => p?.images?.length || 1;
  const prevImg = () => setActive((a) => (a - 1 + imgCount()) % imgCount());
  const nextImg = () => setActive((a) => (a + 1) % imgCount());

  const submitInterest = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/requests", clean({ type: "contact", ...form, property_id: p.id, property_ref: p.reference, subject: `Intérêt bien ${p.reference}` }));
      toast.success(t("detail.interestSent"));
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const submitVisit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await api.post("/requests", clean({ type: "visite", ...visit, property_id: p.id, property_ref: p.reference }));
      toast.success(t("detail.visitSent"));
      setVisit({ name: "", phone: "", email: "", date: "", message: "" });
      document.getElementById("close-visit")?.click();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!p) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-gold border-t-transparent" /></div>;

  const waMsg = `Bonjour IMmOMAXE, je souhaite plus d'informations sur le bien ${p.reference} - ${p.title} (${fmtFCFA(p.short_term ? p.price_night : p.price)}).`;

  return (
    <div className="bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-6">
        <Link to="/acheter" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy" data-testid="back-link"><ArrowLeft className="h-4 w-4" /> {t("detail.back")}</Link>
      </div>

      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 aspect-[16/10] overflow-hidden rounded-xl relative group">
            <img src={p.images?.[active]} alt={p.title} className="h-full w-full object-cover cursor-pointer" data-testid="gallery-main" onClick={() => setLightbox(true)} />
            <button onClick={() => setLightbox(true)} className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-navy shadow hover:bg-white transition-colors" data-testid="gallery-open">
              <Expand className="h-4 w-4" /> Voir les {p.images?.length || 0} photos
            </button>
          </div>
          <div className="grid grid-cols-4 lg:grid-cols-1 gap-3">
            {p.images?.slice(0, 4).map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`aspect-[4/3] overflow-hidden rounded-lg border-2 ${active === i ? "border-gold" : "border-transparent"}`}>
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {p.images?.length > 0 && (
        <Dialog open={lightbox} onOpenChange={setLightbox}>
          <DialogContent className="max-w-5xl bg-navy/95 border-0 p-2 sm:p-4">
            <DialogHeader className="sr-only"><DialogTitle>Photos — {p.title}</DialogTitle><DialogDescription>Galerie du bien</DialogDescription></DialogHeader>
            <div className="relative">
              <img src={p.images[active]} alt={p.title} className="w-full max-h-[75vh] object-contain rounded-lg" data-testid="lightbox-image" />
              {p.images.length > 1 && (<>
                <button onClick={prevImg} className="absolute left-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy hover:bg-white" data-testid="lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={nextImg} className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy hover:bg-white" data-testid="lightbox-next"><ChevronRight className="h-5 w-5" /></button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white" data-testid="lightbox-counter">{active + 1} / {p.images.length}</span>
              </>)}
            </div>
            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-md border-2 ${active === i ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={`lightbox-thumb-${i}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-gold">{p.reference} · {p.type}</span>
              <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold text-navy" data-testid="property-title">{p.title}</h1>
              <p className="mt-2 flex items-center gap-1.5 text-slate-500"><MapPin className="h-4 w-4" /> {p.district ? `${p.district}, ` : ""}{p.city}, {p.country}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={toggleFav} variant="outline" size="icon" data-testid="detail-fav-btn" className="rounded-lg"><Heart className={`h-5 w-5 ${fav ? "fill-red-500 text-red-500" : ""}`} /></Button>
              <Button onClick={share} variant="outline" size="icon" data-testid="detail-share-btn" className="rounded-lg"><Share2 className="h-5 w-5" /></Button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-6 rounded-xl bg-white border border-black/5 p-6">
            {p.surface > 0 && <Spec icon={Maximize} label={t("detail.surface")} value={`${p.surface} m²`} />}
            {p.bedrooms > 0 && <Spec icon={Bed} label={t("detail.bedrooms")} value={p.bedrooms} />}
            {p.bathrooms > 0 && <Spec icon={Bath} label={t("detail.bathrooms")} value={p.bathrooms} />}
            {p.parking > 0 && <Spec icon={Car} label={t("detail.parking")} value={p.parking} />}
          </div>

          <div className="mt-8">
            <h2 className="font-serif text-2xl font-semibold text-navy">{t("detail.description")}</h2>
            <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="property-desc">{p.description}</p>
          </div>

          {p.features?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-serif text-2xl font-semibold text-navy">{t("detail.features")}</h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {p.features.map((f) => <div key={f} className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="h-4 w-4 text-gold" /> {f}</div>)}
              </div>
            </div>
          )}
          {p.equipments?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-serif text-2xl font-semibold text-navy">{t("detail.equipments")}</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.equipments.map((f) => <span key={f} className="rounded-full bg-navy/5 px-3 py-1.5 text-sm text-navy">{f}</span>)}
              </div>
            </div>
          )}

          <div className="mt-8">
            <h2 className="font-serif text-2xl font-semibold text-navy">{t("detail.location")}</h2>
            <div className="mt-4 aspect-[16/7] rounded-xl overflow-hidden border">
              <iframe title="carte" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(`${p.city}, ${p.country}`)}&output=embed`} />
            </div>
          </div>

          {p.short_term && p.house_rules ? (
            <div className="mt-8">
              <h2 className="font-serif text-2xl font-semibold text-navy flex items-center gap-2"><ScrollText className="h-5 w-5 text-gold" /> Règlement du logement</h2>
              <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="house-rules">{p.house_rules}</p>
            </div>
          ) : null}

          {p.short_term && <PropertyReviews propertyId={p.id} />}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-5">
            {p.short_term ? <BookingWidget property={p} /> : (<>
            <div className="rounded-xl bg-navy text-white p-6">
              <span className="text-xs uppercase tracking-widest text-white/50">{p.status}</span>
              <div className="mt-1 font-mono text-3xl font-bold text-gold" data-testid="property-price">
                {fmtFCFA(p.short_term ? p.price_night : p.price)}
                {p.short_term && <span className="text-sm text-white/60"> {t("detail.perNight")}</span>}
                {p.transaction === "location" && !p.short_term && <span className="text-sm text-white/60"> {t("detail.perMonth")}</span>}
              </div>
              <div className="mt-5 space-y-3">
                {p.short_term ? (
                  <Link to={`/location-courte-duree?property=${p.id}`}><Button className="w-full bg-gold text-navy hover:bg-gold-light font-semibold h-11 rounded-lg" data-testid="reserve-btn"><Calendar className="h-4 w-4 mr-2" /> {t("detail.reserve")}</Button></Link>
                ) : (
                  <Dialog>
                    <DialogTrigger asChild><Button className="w-full bg-gold text-navy hover:bg-gold-light font-semibold h-11 rounded-lg" data-testid="request-visit-btn"><Calendar className="h-4 w-4 mr-2" /> {t("detail.requestVisit")}</Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">{t("detail.requestVisit")}</DialogTitle><DialogDescription>{t("detail.visitDesc")}</DialogDescription></DialogHeader>
                      <form onSubmit={submitVisit} className="space-y-4" data-testid="visit-form">
                        <div><Label>{t("detail.name")} *</Label><Input required value={visit.name} onChange={(e) => setVisit({ ...visit, name: e.target.value })} className="mt-1.5" data-testid="visit-name" /></div>
                        <div><Label>{t("detail.phone")} *</Label><Input required value={visit.phone} onChange={(e) => setVisit({ ...visit, phone: e.target.value })} className="mt-1.5" data-testid="visit-phone" /></div>
                        <div><Label>{t("detail.email")}</Label><Input type="email" value={visit.email} onChange={(e) => setVisit({ ...visit, email: e.target.value })} className="mt-1.5" data-testid="visit-email" /></div>
                        <div><Label>{t("detail.date")}</Label><Input type="date" value={visit.date} onChange={(e) => setVisit({ ...visit, date: e.target.value })} className="mt-1.5" data-testid="visit-date" /></div>
                        <div><Label>{t("detail.message")}</Label><Textarea value={visit.message} onChange={(e) => setVisit({ ...visit, message: e.target.value })} className="mt-1.5" data-testid="visit-message" /></div>
                        <Button type="submit" disabled={sending} className="w-full bg-navy text-white h-11 rounded-lg" data-testid="visit-submit">{sending ? t("detail.sending") : t("detail.send")}</Button>
                        <button id="close-visit" type="button" className="hidden" />
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
                <WhatsAppButton label={t("detail.whatsapp")} message={waMsg} className="w-full" testid="detail-whatsapp" />
              </div>
            </div>

            <form onSubmit={submitInterest} className="rounded-xl bg-white border border-black/5 p-6 space-y-3" data-testid="interest-form">
              <h3 className="font-serif text-xl font-semibold text-navy">{t("detail.interested")}</h3>
              <Input required placeholder={t("detail.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="interest-name" />
              <Input required placeholder={t("detail.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="interest-phone" />
              <Input type="email" placeholder={t("detail.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="interest-email" />
              <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="interest-message" />
              <Button type="submit" disabled={sending} className="w-full bg-navy text-white h-11 rounded-lg" data-testid="interest-submit">{sending ? t("detail.sending") : t("detail.send")}</Button>
            </form>
            </>)}
          </div>
        </div>
      </div>
    </div>
  );
}

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5">
    <Icon className="h-5 w-5 text-gold" />
    <div><div className="text-xs text-slate-400">{label}</div><div className="font-semibold text-navy">{value}</div></div>
  </div>
);
